import { FullScreenLoader } from 'components/shared/loaders/FullScreenLoader/FullScreenLoader';
import {
  ROUTE_CHILD_REGISTER,
  ROUTE_INSTITUTIONS,
  ROUTE_POLLS,
  ROUTE_REPORTS,
  ROUTE_SCHOOL_DISTRICTS,
  ROUTE_SCHOOL_OBLIGATION,
  ROUTE_TEMPLATES,
  ROUTE_SETTINGS,
  ROUTE_OFFICIALS,
  ROUTE_PANEL,
  ROUTE_PANEL_EMPTY_PAGE,
  ROUTE_PANEL_MESSAGES,
  ROUTE_SEARCH,
} from 'config/routes';
import { push } from 'utils/navigation/navigate';
import { relativePath } from 'utils/navigation/relativePath';
import React from 'react';
import { Sidebar } from 'components/layout/panel/Sidebar/Sidebar';
import { TitleBarContainer } from 'components/layout/panel/TitleBar/TitleBarContainer';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, Navigate } from 'react-router-dom';
import { fetchDictionaries } from 'store/general';
import { areDictionariesReadySelector } from 'store/general/general.selectors';
import { Main } from 'components/shared/layout/Main/Main';
import { NotFoundView } from 'views/Errors/NotFound.view';
import { ChildRegisterView } from 'views/Panel/ChildRegister/ChildRegister.view';
import { useStyles } from 'views/Panel/Panel.styles';
import { Footer } from 'components/shared/layout/Footer/Footer';
import { PollsView } from 'views/Panel/Polls/Polls.view';
import { ReportsView } from 'views/Panel/Reports/Reports.view';
import { TemplatesView } from 'views/Panel/Templates/Templates.view';
import { InstitutionsView } from './Institutions/Institutions.view';
import { SchoolDistrictsView } from 'views/Panel/SchoolDistricts/SchoolDistricts.view';
import { SchoolObligationView } from 'views/Panel/SchoolObligation/SchoolObligation.view';
import { SettingsView } from 'views/Panel/Settings/Settings.view';
import { OfficialsView } from 'views/Panel/Officials/Officials.view';
import { navigation } from 'components/layout/panel/Sidebar/navigation/navigation';
import { helpersMultipeCheckAccess } from 'components/shared/hooks';
import { userOfficialRole, userOfficialPermissions, userOfficialPermissionsReady, userId } from 'store/user';
import { EmptyView } from 'views/Panel/Empty/Empty.view';
import { MessagesView } from 'views/Panel/Messages/Messages.view';
import { SearchView } from 'views/Panel/Search/Search.view';

const handleFor = (role, permissions) => (navigation) => {
  for (const single of navigation) {
    const isAccess = helpersMultipeCheckAccess(single.resourcesGroups, single.accessType, role, permissions);
    if (!isAccess) continue;
    if (!single.children) return single;
    const permissionChildren = handleFor(role, permissions)(single.children);
    if (permissionChildren) return permissionChildren;
  }
};

const BaseRoute = () => {
  const role = useSelector(userOfficialRole);
  const permissions = useSelector(userOfficialPermissions);
  const permissionsReady = useSelector(userOfficialPermissionsReady);
  const activeElement = handleFor(role, permissions)(navigation);
  if (permissionsReady && !activeElement) {
    push(ROUTE_PANEL_EMPTY_PAGE);
    return null;
  }
  if (!activeElement) return null;
  if (permissionsReady && !activeElement.path) {
    push(ROUTE_PANEL_MESSAGES);
    return null;
  }
  return <Navigate to={activeElement.path} replace />;
};

export const PanelView: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const dictionariesReady = useSelector(areDictionariesReadySelector);
  const userIdValue = useSelector(userId);

  // Only fetch dictionaries if user is authenticated and dictionaries are not ready
  // This prevents calling /dictionaries before authorization
  if (userIdValue && !dictionariesReady) {
    dispatch(fetchDictionaries());
  }

  const renderMain = () => {
    return (
      <>
        <Sidebar />
        <Main>
          <Routes>
            <Route path={relativePath(ROUTE_PANEL, ROUTE_PANEL)} element={<BaseRoute />} />
            <Route path={relativePath(`${ROUTE_INSTITUTIONS}/*`, ROUTE_PANEL)} element={<InstitutionsView />} />
            <Route path={relativePath(`${ROUTE_CHILD_REGISTER}/*`, ROUTE_PANEL)} element={<ChildRegisterView />} />
            <Route path={relativePath(`${ROUTE_SCHOOL_DISTRICTS}/*`, ROUTE_PANEL)} element={<SchoolDistrictsView />} />
            <Route path={relativePath(`${ROUTE_SCHOOL_OBLIGATION}/*`, ROUTE_PANEL)} element={<SchoolObligationView />} />
            <Route path={relativePath(`${ROUTE_TEMPLATES}/*`, ROUTE_PANEL)} element={<TemplatesView />} />
            <Route path={relativePath(`${ROUTE_POLLS}/*`, ROUTE_PANEL)} element={<PollsView />} />
            <Route path={relativePath(`${ROUTE_REPORTS}/*`, ROUTE_PANEL)} element={<ReportsView />} />
            <Route path={relativePath(`${ROUTE_SETTINGS}/*`, ROUTE_PANEL)} element={<SettingsView />} />
            <Route path={relativePath(`${ROUTE_OFFICIALS}/*`, ROUTE_PANEL)} element={<OfficialsView />} />
            <Route path={relativePath(ROUTE_PANEL_MESSAGES, ROUTE_PANEL)} element={<MessagesView />} />
            <Route path={relativePath(ROUTE_PANEL_EMPTY_PAGE, ROUTE_PANEL)} element={<EmptyView />} />
            <Route path={relativePath(ROUTE_SEARCH, ROUTE_PANEL)} element={<SearchView />} />
            <Route path="*" element={<NotFoundView />} />
          </Routes>
        </Main>
      </>
    );
  };

  return (
    <div className={classes.root}>
      <TitleBarContainer />
      <div className={classes.content}>{dictionariesReady ? renderMain() : <FullScreenLoader />}</div>
      <Footer />
    </div>
  );
};
